/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import type { Deal } from '@/services/requests/housien-services/DealsService';
import {
  Button,
  DETAIL_ACTION_SECONDARY,
  DetailStatStrip,
  Text,
  type DetailStatItem,
} from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import { useSaleSettings } from '@/features/sales/hooks/useSaleSettings';

import { formatDealMoney, type DealViewState } from '../utils/deal-state';
import { dealValues, type DealValues } from '../utils/deal-values';

/**
 * The money band across the top of the deal detail screen.
 *
 * It used to be three amounts — asked, offered, accepted — which said
 * what had happened but not what it meant. The five figures here answer
 * the questions a broker actually opens the screen with: how far apart
 * the two sides still are, what the agency stands to make, and how long
 * this has been going on.
 */

export interface DealSummaryCardsProps {
  agencyId: string;
  deal: Deal;
  view: DealViewState;
  /** Opens the proposal form. Only reachable while every amount is unset. */
  onRecordProposal?: () => void;
  /**
   * The attendance's actions, shown at the end of the strip.
   *
   * They used to sit in a band of their own above this one. Trimmed to
   * three buttons, that band was a full-width row holding nothing else
   * -- two strips where the reader only ever needed one.
   */
  actions?: ReactNode;
}

/**
 * Returns the accent colour: active when the value is present, muted otherwise.
 */
function accentFor(value: unknown, activeVar = 'var(--seed-base)'): string {
  return value != null ? activeVar : 'var(--text-tertiary-soft)';
}

/**
 * Builds the asking-price item.
 */
function askingItem(values: DealValues, t: TFunction): DetailStatItem {
  return {
    id: 'asking',
    label: t('detail.summary.reference'),
    value: formatDealMoney(values.asking) ?? '—',
    accent: accentFor(values.asking),
    hint: t('detail.summary.askingHint'),
  };
}

/**
 * Builds the standing-offer item.
 */
function standingItem(values: DealValues, t: TFunction): DetailStatItem {
  return {
    id: 'standing',
    label: t('detail.summary.proposed'),
    value: formatDealMoney(values.standing) ?? '—',
    accent: accentFor(values.standing),
    hint: values.standingAgeDays == null
      ? undefined
      : t('detail.summary.standingHint', {
        side: t(`detail.summary.side.${values.standingSide ?? 'client'}`),
        days: values.standingAgeDays,
      }),
  };
}

/**
 * Builds the gap item.
 */
function gapItem(values: DealValues, t: TFunction): DetailStatItem {
  return {
    id: 'gap',
    label: t('detail.summary.gap'),
    value: formatDealMoney(values.gap) ?? '—',
    accent: accentFor(values.gap, 'var(--text-primary)'),
    hint: values.gapPercent == null
      ? undefined
      : t('detail.summary.gapHint', { percent: values.gapPercent.toFixed(1) }),
  };
}

/**
 * Builds the commission item.
 */
function commissionItem(values: DealValues, t: TFunction): DetailStatItem {
  const hintKey = values.commissionFromDefault
    ? 'detail.summary.commissionDefaultHint'
    : 'detail.summary.commissionHint';
  return {
    id: 'commission',
    label: t('detail.summary.commission'),
    value: formatDealMoney(values.commission) ?? '—',
    accent: accentFor(values.commission, 'var(--text-primary)'),
    hint: values.commissionPercent == null
      ? undefined
      : t(hintKey, { percent: values.commissionPercent }),
  };
}

/**
 * Builds the open-for item.
 */
function openForItem(deal: Deal, values: DealValues, t: TFunction): DetailStatItem {
  return {
    id: 'openFor',
    label: t('detail.summary.openFor'),
    value: values.openDays == null
      ? '—'
      : t('detail.summary.dayCount', { count: values.openDays }),
    accent: 'var(--text-primary)',
    hint: t('detail.summary.openSince', { date: formatLocalizedDate(deal.created_at) }),
  };
}

/**
 * Builds the five figures, each falling back to an em dash rather than
 * vanishing — a band that changes length as facts arrive reads as a
 * different band each time.
 *
 * @param deal - The attendance.
 * @param values - Its derived figures.
 * @param t - The deals-namespace translation function.
 * @returns The strip items, in reading order.
 */
function buildItems(deal: Deal, values: DealValues, t: TFunction): DetailStatItem[] {
  return [
    askingItem(values, t),
    standingItem(values, t),
    gapItem(values, t),
    commissionItem(values, t),
    openForItem(deal, values, t),
  ];
}

/**
 * The attendance's value band.
 *
 * @param props - The agency, the deal, its view state, and the record action.
 * @returns The rendered strip.
 */
export function DealSummaryCards({
  agencyId, deal, view, onRecordProposal, actions,
}: DealSummaryCardsProps) {
  const { t } = useTranslation('features/deals');
  const { defaultCommissionPercentage } = useSaleSettings(agencyId);
  const values = dealValues(deal, defaultCommissionPercentage);
  const items = buildItems(deal, values, t);

  const noMoney = values.asking == null && values.standing == null;
  // Recording an offer is a running-deal action. The Propostas tab gates it
  // the same way; without the gate here the empty state offered it on a
  // closed or paused deal, where the tab then refuses to record it.
  const canRecord = view.lifecycle === 'running' && onRecordProposal != null;

  return (
    <DetailStatStrip
      testId="deal-summary-strip"
      items={items}
      action={
        <>
          {noMoney && (
            <>
              <Text
                component="span"
                className="text-[length:var(--text-meta)] text-(--text-tertiary-soft)"
              >
                {t('detail.summary.noValuesRecorded')}
              </Text>
              {canRecord && (
                <Button
                  size="custom"
                  variant="none"
                  className={DETAIL_ACTION_SECONDARY}
                  onClick={onRecordProposal}
                  data-testid="strip-record-proposal-button"
                >
                  {t('detail.actions.recordProposal')}
                </Button>
              )}
            </>
          )}
          {actions}
        </>
      }
    />
  );
}
